/**
 * # Heavy-POLYLINE SEQEND passthrough (SL-22 F1) — now the MALFORMED-FILE net
 *
 * A legacy heavy `POLYLINE` re-emits as `POLYLINE` + `VERTEX`* + `SEQEND`.
 *
 * ## What changed
 *
 * The heavy polyline family is now FULLY TYPED (`polylineEntitySchema`): the
 * dxf-io grouping pass folds the VERTEX run AND the terminating SEQEND onto the
 * owning POLYLINE, and the writer re-emits all three from the typed model. On the
 * normal path this module's SEQEND reconstruction therefore no longer fires — the
 * terminator (with its own handle, owner and layer) rides the typed
 * `PolylineEntity`, and `isPolylineTerminatingSeqend` cannot match because `prev`
 * is a TYPED `POLYLINE`, never an `unsupported-entity` one.
 *
 * ## Why it is still live, and why it must stay
 *
 * `parseSupportedEntity` can THROW on a malformed record, and the BLOCKS walk
 * (`sectionParsers.ts`) then falls back to `unsupported-entity` for that record.
 * A heavy polyline whose POLYLINE or VERTEX fails to parse therefore STILL
 * reaches the raw-passthrough path — and its typed SEQEND would once again be
 * dropped, re-opening the exact DATA LOSS SL-22 closed (an unterminated
 * `POLYLINE … VERTEX …` sequence plus loss of the SEQEND's handle / owner /
 * layer). This module keeps that net in place.
 *
 * Either way the terminator is PRESERVED from the source, never inferred or
 * synthesised ([[feedback-roundtrip-preserve-source-never-infer]]).
 *
 * An INSERT's SEQEND is CONSUMED by the parser (`attributesFollow` → the INSERT's
 * `seqendHandle`) and never appears standalone in the entity stream, so it is
 * excluded here BY CONSTRUCTION: a standalone SEQEND is treated as a POLYLINE
 * terminator ONLY when its immediately-preceding entity is an unsupported
 * `POLYLINE` (an empty heavy polyline) or `VERTEX` (its last vertex) — never after
 * an INSERT/ATTRIB. A truly-orphan SEQEND (no preceding heavy-polyline body, a
 * malformed file) is left untouched (still skipped), matching the plan's scope.
 *
 * Source of truth: Autodesk DXF reference (POLYLINE/VERTEX/SEQEND grouping) +
 * ezdxf 1.4.4 (`entities/polyline.py`). AutoCAD MCP is BANNED.
 */
import {
  serializeSeqendPassthroughGroups,
  type DxfEntity,
} from "@dxf-ai-editor/dxf-io";

import type { RawEntityGroup } from "./model";

/**
 * True when `entity` is a standalone SEQEND that terminates a legacy heavy
 * POLYLINE — i.e. `prev` is an unsupported `POLYLINE` or `VERTEX`. Excludes an
 * INSERT/ATTRIB-terminating SEQEND (never standalone) and a truly-orphan SEQEND.
 */
export function isPolylineTerminatingSeqend(
  prev: DxfEntity | undefined,
  entity: DxfEntity,
): boolean {
  if (entity.kind !== "supported-entity" || entity.type !== "SEQEND") {
    return false;
  }
  return (
    prev !== undefined &&
    prev.kind === "unsupported-entity" &&
    (prev.type === "POLYLINE" || prev.type === "VERTEX")
  );
}

/**
 * The `RawEntityGroup` to capture for `entity` given its predecessor `prev`, or
 * `null` when `entity` carries no raw-passthrough payload. Two sources:
 *
 *   • an `unsupported-entity` — its groups verbatim (the header `0/<TYPE>` group
 *     is stripped; `entityType` carries it separately, and the writer re-prepends
 *     it). This is the pre-existing behaviour, now routed through here so a SINGLE
 *     ordered walk covers BOTH the verbatim and the reconstructed-SEQEND cases.
 *   • a POLYLINE-terminating `SEQEND` — reconstructed from the typed entity via the
 *     dxf-io canonical serializer (preserving handle / owner / layer / xdata, and
 *     always emitting group 8 — SL-22 F1 + F3).
 *
 * Preserving iteration order in the caller keeps the reconstructed SEQEND directly
 * after its heavy-polyline body so the `POLYLINE … VERTEX … SEQEND` grouping stays
 * contiguous on export.
 */
export function rawPassthroughGroupForEntity(
  prev: DxfEntity | undefined,
  entity: DxfEntity,
): RawEntityGroup | null {
  if (entity.kind === "unsupported-entity") {
    return {
      entityType: entity.type,
      groups: entity.groups.slice(1).map((g) => ({
        code: g.code,
        value: g.value,
      })),
    };
  }
  if (isPolylineTerminatingSeqend(prev, entity) && entity.type === "SEQEND") {
    // `serializeSeqendPassthroughGroups` returns the full groups incl. the leading
    // `0 SEQEND` header — strip it, mirroring the unsupported-entity `slice(1)`.
    return {
      entityType: "SEQEND",
      groups: serializeSeqendPassthroughGroups(entity)
        .slice(1)
        .map((g) => ({ code: g.code, value: g.value })),
    };
  }
  return null;
}
